import { ControlledIFrameElement, createControlledFrame } from '@fullstory/controlled-iframe';
import type { PlaybackContext } from '@fullstory/renderer-shared';
import type { DocumentEvents } from '../events';
import { FsDocument, FsRootDocument } from './fsdocument';
import type { FsDomEventHandlers } from './fsdomeventhandlers';
import type { IFsDocumentContainer } from './ifsdocumentcontainer';

/**
 * A container node for the root FsDocument.
 *
 * This node is somewhat special in that it represents a bridge between the
 * DOM of the playback container and the replay vDOM. It's not an FsNode or an
 * FsDocument; rather, it contains and manages an FsDocument and connects it
 * into the DOM of the outer page.
 *
 * The contained root document can be updated by calling
 * {@link FsRootDocumentContainer#setContentDocument()}.
 * After a change, the new state must be flushed to the DOM by calling
 * {@link FsRootDocumentContainer#flush()}.
 */
export class FsRootDocumentContainer implements IFsDocumentContainer {
  private _contentDoc: FsRootDocument | undefined = undefined;
  public frame: ControlledIFrameElement | null = null;

  /**
   * @param containerNode - The DOM node into which the root FsDocument should
   * be mounted. This node shouldn't contain any other children (and if they do
   * exist, they'll be removed).
   */
  constructor(
    public readonly ctx: PlaybackContext,
    public readonly containerNode: Node,
    public readonly handlers: FsDomEventHandlers,
  ) {
    this._detach();
  }

  /**
   * Set the new root document that should live within this container.
   * @param events - The DocumentEvents object describing the new root document, or
   * undefined if the container is now empty.
   * @returns the new root FsDocument, or undefined if the container is now empty.
   */
  setContentDocument(events: DocumentEvents): FsRootDocument;
  setContentDocument(events: undefined): undefined;
  setContentDocument(events: DocumentEvents | undefined): FsRootDocument | undefined;
  setContentDocument(events: DocumentEvents | undefined): FsRootDocument | undefined {
    if (events === undefined) {
      this._removeContentDocument();
      return undefined;
    }

    if (!this._contentDoc || (this._contentDoc && !this._contentDoc.path.equals(events.path))) {
      this._detach();
      const doc = new FsDocument({
        ctx: this.ctx,
        events,
        parent: null,
        handlers: this.handlers,
      });
      if (!doc.isRoot()) {
        throw new Error('Code bug: Expected doc to be root.');
      }
      this._contentDoc = doc;
    }

    return this._contentDoc;
  }

  private _removeContentDocument(): void {
    if (!this._contentDoc) {
      return;
    }

    const contentDoc = this._contentDoc;
    this._contentDoc = undefined;
    contentDoc.detach();
    this._detach();
  }

  private _detach(): void {
    this.frame = null;
    while (this.containerNode.firstChild) {
      this.containerNode.removeChild(this.containerNode.firstChild);
    }
  }

  getContentDocument(): FsRootDocument | undefined {
    return this._contentDoc;
  }

  /** Update the DOM to reflect the current content document. */
  async flush(): Promise<void> {
    if (this.frame) {
      return;
    }

    const containerDoc = this.containerNode.ownerDocument!;
    if (this._contentDoc) {
      this.frame = this._contentDoc.createFrame(containerDoc, 'iframe');
    } else {
      this.frame = createControlledFrame(this.ctx, containerDoc, `<!DOCTYPE html>`, 'iframe');
    }

    this.frame.style.setProperty('width', '100%');
    this.frame.style.setProperty('height', '100%');
    this.containerNode.appendChild(this.frame);

    if (this._contentDoc) {
      await this._contentDoc.attachToFrame(this.frame);
    }
  }
}
